/**
 * One acquisition run, in full.
 *
 * Three things the list cannot show:
 *
 *   1. **The exact filters, in plain English.** Rendered by the API from the
 *      stored `filters_json`, so a run started a year ago explains itself even
 *      if the dialog's defaults have changed since.
 *   2. **The per-company outcome trail**, served from `sic_acquisition_items` -
 *      a table, never a JSON blob, because one run here routinely produces
 *      170,000 rows. Searchable, filterable by outcome, and paged.
 *   3. **CSV export** of whatever the filters currently select.
 *
 * The live progress card is the same component the log list uses. A run opened
 * while it is still going keeps updating, controls and all.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AcquisitionProgress } from '../components/AcquisitionProgress.jsx';
import CompanyProfile from '../components/CompanyProfile.jsx';
import {
  IconAlert,
  IconChevronLeft,
  IconClose,
  IconDownload,
  IconFilter,
  IconSearch,
} from '../components/Icons.jsx';
import { acquisitionApi } from '../lib/api.js';

const ITEM_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 250;

/** What each outcome means, said once rather than guessed at per row. */
const OUTCOME_META = {
  new: {
    label: 'New',
    tone: 'badge-ok',
    tip: 'This company was not in the registry before this run.',
  },
  updated: {
    label: 'Updated',
    tone: 'badge-info',
    tip: 'The company was already stored - from an earlier run or another SIC code it also files - and this run refreshed it. A company legitimately arrives under several codes, so this is normal, not a duplicate.',
  },
  skipped: {
    label: 'Skipped',
    tone: 'badge-warn',
    tip: 'The record was returned but not written, usually because it was missing a company number.',
  },
  error: {
    label: 'Error',
    tone: 'badge-error',
    tip: 'This company failed to write. One bad company never stops a run - it is counted, logged and passed over.',
  },
};

function formatNumber(value) {
  return typeof value === 'number' ? value.toLocaleString() : '—';
}

/**
 * What an outstanding enrichment stage means, for the tooltip on the second
 * badge in the Outcome cell. Keyed by `item.enrichmentStatus.stage`, which is
 * null once every stage the run's depth requires is done - that case reads
 * its own fixed tip below instead.
 */
const ENRICHMENT_STAGE_TIP = {
  profile: 'The profile fetch has not run for this company yet: accounts and confirmation-statement dates, previous names, insolvency and charges flags.',
  financials: 'Filed accounts have not been read for this company yet.',
  directors: 'Director resolution has not run for this company yet.',
  websites: 'Website discovery has not run for this company yet.',
  emails: 'Email guessing has not run for this company yet.',
};

const ENRICHMENT_DONE_TIP = 'Every enrichment pass this run requested has completed for this company.';

/**
 * The item trail's sort choices, one `<select>` the same way the registry
 * page's "Sort" control works - not clickable headers, so the two pages stay
 * consistent. Each value is `${sortBy}-${sortDir}`; `written` (insertion
 * order) is the default and needs no cursorValue when paging (see
 * `SORT_VALUE_FIELD` below), everything else does.
 */
const SORT_OPTIONS = [
  { value: 'written-desc', label: 'Written (newest first)' },
  { value: 'written-asc', label: 'Written (oldest first)' },
  { value: 'name-asc', label: 'Name (A–Z)' },
  { value: 'name-desc', label: 'Name (Z–A)' },
  { value: 'number-asc', label: 'Company number (A–Z)' },
  { value: 'number-desc', label: 'Company number (Z–A)' },
  { value: 'outcome-asc', label: 'Outcome (A–Z)' },
  { value: 'outcome-desc', label: 'Outcome (Z–A)' },
];

/** Maps a non-`written` sort to the item field its cursorValue is read from. */
const SORT_VALUE_FIELD = {
  name: 'companyName',
  number: 'companyNumber',
  outcome: 'outcome',
};

/**
 * The per-page cursorValue trail lives in the URL the same way `cursors`
 * does, comma-joined - but a company name can itself contain a comma, so each
 * entry is percent-encoded before joining. `encodeURIComponent` never emits a
 * literal comma, so splitting the joined string back apart is unambiguous.
 */
function encodeCursorValues(values) {
  return values.map((value) => encodeURIComponent(value)).join(',');
}

function decodeCursorValues(raw) {
  return raw === '' ? [] : raw.split(',').map((value) => decodeURIComponent(value));
}

export default function RunDetailPage() {
  const { uuid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);

  const [items, setItems] = useState(null);
  const [itemTotal, setItemTotal] = useState(0);
  const [outcomeCounts, setOutcomeCounts] = useState(null);
  const [itemsLoading, setItemsLoading] = useState(false);

  const outcome = searchParams.get('outcome') ?? '';
  const search = searchParams.get('search') ?? '';
  const detail = searchParams.get('detail') ?? '';
  const sortBy = searchParams.get('sortBy') || 'written';
  const sortDir = searchParams.get('sortDir') || 'desc';
  // Which row's registry popup is open, if any - kept in the URL like every
  // other piece of view state on this page, so a linked-to company profile
  // survives a refresh instead of vanishing.
  const openCompany = searchParams.get('company') ?? '';

  // Keyset paging, not offset. `cursors` is the trail of item ids the operator
  // has paged through - each entry is "the last id on the page before this
  // one" - so the current page's cursor is the last entry, and "how many
  // pages deep" (for the "Showing X-Y" text) is just its length. Kept in the
  // URL, comma-joined, the same way `offset` used to be: paging position stays
  // bookmarkable and survives a refresh.
  const cursorStack = (searchParams.get('cursors') ?? '')
    .split(',')
    .map(Number)
    .filter((value) => Number.isInteger(value) && value > 0);
  const cursor = cursorStack.length > 0 ? cursorStack[cursorStack.length - 1] : undefined;
  const pageIndex = cursorStack.length;

  // Sorting by anything other than `written` needs more than `id` to seek a
  // page - see `buildItemCursorWhere` on the API. `cursorValues` is that
  // trail's other half, kept in step with `cursors` (same length, same
  // ordering) but only read when it is actually needed.
  const cursorValueStack = decodeCursorValues(searchParams.get('cursorValues') ?? '');
  const cursorValue =
    sortBy !== 'written' && cursorValueStack.length > 0
      ? cursorValueStack[cursorValueStack.length - 1]
      : undefined;

  // The search and detail boxes are local so typing stays responsive; the URL
  // catches up after the debounce.
  const [searchDraft, setSearchDraft] = useState(search);
  const [detailDraft, setDetailDraft] = useState(detail);

  /** Sets any number of query params at once, atomically. */
  const setParams = useCallback(
    (updates, { resetPage = true } = {}) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(updates)) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          // Any change to what is being looked at goes back to page one.
          if (resetPage) {
            next.delete('cursors');
            next.delete('cursorValues');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setParam = useCallback((key, value) => setParams({ [key]: value }), [setParams]);

  // Opening or closing the popup is not a change to what the trail is
  // filtered or paged to, so - unlike setParam - it never resets the page.
  const openCompanyProfile = useCallback(
    (companyNumber) => setParams({ company: companyNumber }, { resetPage: false }),
    [setParams],
  );
  const closeCompanyProfile = useCallback(
    () => setParams({ company: '' }, { resetPage: false }),
    [setParams],
  );

  useEffect(() => {
    if (!openCompany) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeCompanyProfile();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openCompany, closeCompanyProfile]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchDraft !== search) setParam('search', searchDraft.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchDraft, search, setParam]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (detailDraft !== detail) setParam('detail', detailDraft.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [detailDraft, detail, setParam]);

  useEffect(() => {
    acquisitionApi
      .get(uuid)
      .then((result) => {
        setRun(result.run);
        setError(null);
      })
      .catch((caught) => setError(caught.message ?? 'Could not load this run.'));
  }, [uuid]);

  const loadItems = useCallback(async () => {
    setItemsLoading(true);
    try {
      const result = await acquisitionApi.items(uuid, {
        limit: ITEM_PAGE_SIZE,
        cursor,
        cursorValue,
        sortBy: sortBy !== 'written' ? sortBy : undefined,
        sortDir: sortDir !== 'desc' ? sortDir : undefined,
        outcome: outcome || undefined,
        search: search || undefined,
        detail: detail || undefined,
        // The counts are a GROUP BY over every row of the run, so they are
        // fetched once on the unfiltered first page and then reused.
        withCounts: outcomeCounts === null,
      });
      setItems(result.items);
      setItemTotal(result.total);
      if (result.outcomeCounts) setOutcomeCounts(result.outcomeCounts);
      setError(null);
    } catch (caught) {
      setError(caught.message ?? 'Could not load the company trail.');
    } finally {
      setItemsLoading(false);
    }
    // `outcomeCounts` is deliberately not a dependency: it is set by this
    // effect, and depending on it would run the whole thing a second time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, cursor, cursorValue, sortBy, sortDir, outcome, search, detail]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  if (error && !run) {
    return (
      <div className="page">
        <div className="alert alert-error">
          <span className="alert-icon">
            <IconAlert />
          </span>
          <span className="alert-body">
            <span className="alert-title">Could not open this run</span>
            {error}
          </span>
        </div>
        <Link className="btn" to="/log">
          <IconChevronLeft />
          Back to the log
        </Link>
      </div>
    );
  }

  if (!run) {
    return (
      <div className="page">
        <div className="loading-panel">
          <span className="spinner" />
          Loading the run
        </div>
      </div>
    );
  }

  const showingFrom = itemTotal === 0 ? 0 : pageIndex * ITEM_PAGE_SIZE + 1;
  const showingTo = Math.min((pageIndex + 1) * ITEM_PAGE_SIZE, itemTotal);
  const isFiltered = Boolean(outcome || search || detail);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <Link className="btn btn-ghost btn-sm" to="/log">
            <IconChevronLeft />
            Acquisition log
          </Link>
          <h1 className="page-title">
            <span className="mono">{run.sicCode}</span> {run.sicDescription}
          </h1>
          <p className="page-description">
            Run {run.uuid.slice(0, 8)} · started{' '}
            {run.startedAt ? new Date(run.startedAt).toLocaleString() : 'not yet'}
            {run.batchId ? ' · part of a multi-code batch' : ''}
          </p>
        </div>
      </div>

      {/* Every top-level block on this page (the progress/asked-for row, the
          company trail) shares one consistent vertical rhythm here, same as
          Settings' `.col` - `.page` itself has no gap between its direct
          children, so without this the blocks below the header would sit
          flush against each other. */}
      <div className="col" style={{ gap: 'var(--sp-6)' }}>
        <div className="run-detail-top">
          {/* The live card: progress, counters and the pause/resume/cancel
              controls, identical to the log list so a run opened mid-flight
              behaves the same in both places. */}
          <AcquisitionProgress
            run={run}
            onChanged={(updated) => {
              setRun(updated);
              // A finished run has written its last items; refresh the trail
              // so the final rows appear without a manual reload.
              if (!['queued', 'running'].includes(updated.status)) void loadItems();
            }}
          />

          {/* --- What was asked for ------------------------------------ */}
          <section className="card">
            <div className="card-header">
              <span className="card-title">What this run was asked for</span>
            </div>
            <div className="card-body">
              <dl className="kv-grid">
                {(run.filterPhrases ?? []).map((phrase) => (
                  <div className="kv" key={phrase.label}>
                    <dt className="kv-key">{phrase.label}</dt>
                    <dd className="kv-value">{phrase.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        </div>

        {/* --- The per-company trail -------------------------------------- */}
        <section className="card">
          <div className="card-header">
            <span className="card-title">Every company this run touched</span>
            <span className="card-header-actions">
              {/*
                A plain link, not a fetch. The endpoint streams the CSV and the
                browser writes it straight to disk with the session cookie
                attached; pulling 12 MB through the JS client to hand back to a
                Blob is exactly what the streaming endpoint avoids.
              */}
              <a
                className="btn btn-sm tip tip-below tip-left"
                href={acquisitionApi.exportUrl(uuid, {
                  outcome: outcome || undefined,
                  search: search || undefined,
                  detail: detail || undefined,
                })}
                data-tip={
                  isFiltered
                    ? 'Downloads the filtered list you are looking at, as CSV.'
                    : 'Downloads every company this run touched, as CSV: company number, name, outcome, detail and the time it was written.'
                }
              >
                <IconDownload />
                Export CSV
              </a>
            </span>
          </div>

          <div className="card-body">
            <div className="filter-bar filter-bar-tight">
              <div className="search-field">
                <span className="search-field-icon">
                  <IconSearch />
                </span>
                <input
                  className="input"
                  type="search"
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                  placeholder="Company name or number"
                  aria-label="Search the companies this run touched"
                />
              </div>

              <div className="search-field">
                <span className="search-field-icon">
                  <IconFilter />
                </span>
                <input
                  className="input"
                  type="search"
                  value={detailDraft}
                  onChange={(event) => setDetailDraft(event.target.value)}
                  placeholder="Detail contains (skip or error reason)"
                  aria-label="Filter the companies this run touched by their detail text"
                />
              </div>

              <label className="filter-control">
                <span className="filter-label">Sort</span>
                <select
                  className="select"
                  value={`${sortBy}-${sortDir}`}
                  onChange={(event) => {
                    const [nextSortBy, nextSortDir] = event.target.value.split('-');
                    setParams({
                      sortBy: nextSortBy === 'written' ? '' : nextSortBy,
                      sortDir: nextSortDir === 'desc' ? '' : nextSortDir,
                    });
                  }}
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <div className="pill-row">
                <button
                  type="button"
                  className={`pill${outcome === '' ? ' is-active' : ''}`}
                  onClick={() => setParam('outcome', '')}
                >
                  All
                  {outcomeCounts ? (
                    <span className="pill-count">
                      {formatNumber(
                        Object.values(outcomeCounts).reduce((sum, value) => sum + value, 0),
                      )}
                    </span>
                  ) : null}
                </button>

                {Object.entries(OUTCOME_META).map(([value, meta]) => (
                  <button
                    key={value}
                    type="button"
                    className={`pill${outcome === value ? ' is-active' : ''} tip tip-below`}
                    onClick={() => setParam('outcome', value)}
                    data-tip={meta.tip}
                  >
                    {meta.label}
                    {outcomeCounts ? (
                      <span className="pill-count">{formatNumber(outcomeCounts[value] ?? 0)}</span>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {items === null ? (
            <div className="loading-panel">
              <span className="spinner" />
              Loading the company trail
            </div>
          ) : items.length === 0 ? (
            <div className="empty">
              <h3 className="empty-title">
                {isFiltered ? 'Nothing matches these filters' : 'This run has no company trail yet'}
              </h3>
              <p className="empty-description">
                {isFiltered
                  ? 'Clear the search and detail boxes or choose All to see every company this run touched.'
                  : 'A run writes its trail as it goes. If the run is still queued there is nothing to show yet.'}
              </p>
            </div>
          ) : (
            <>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Company number</th>
                      <th scope="col">Name</th>
                      <th scope="col">Outcome</th>
                      <th scope="col">Detail</th>
                      <th scope="col">Written</th>
                    </tr>
                  </thead>
                  <tbody className={itemsLoading ? 'is-loading' : ''}>
                    {items.map((item) => {
                      const meta = OUTCOME_META[item.outcome] ?? {
                        label: item.outcome,
                        tone: '',
                        tip: null,
                      };
                      return (
                        <tr
                          key={item.id}
                          className="is-clickable"
                          onClick={() => openCompanyProfile(item.companyNumber)}
                        >
                          <td className="mono">
                            <a
                              className="link-external"
                              href={`https://find-and-update.company-information.service.gov.uk/company/${item.companyNumber}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {item.companyNumber}
                            </a>
                          </td>
                          <td>{item.companyName}</td>
                          <td>
                            <span className="badge-stack">
                              <span className={`badge ${meta.tone}`}>{meta.label}</span>
                              {/* Where this company stands against the run's own
                                  enrichment depth - null for a `list`-depth run
                                  (nothing beyond the write happens) and for a
                                  skipped/error row (no company to check). */}
                              {item.enrichmentStatus ? (
                                <span
                                  className={`badge badge-sm ${item.enrichmentStatus.done ? 'badge-ok' : 'badge-warn'} tip tip-below`}
                                  data-tip={
                                    item.enrichmentStatus.done
                                      ? ENRICHMENT_DONE_TIP
                                      : (ENRICHMENT_STAGE_TIP[item.enrichmentStatus.stage] ?? null)
                                  }
                                >
                                  {item.enrichmentStatus.label}
                                </span>
                              ) : null}
                            </span>
                          </td>
                          <td className={item.detail ? '' : 'value-absent'}>{item.detail ?? '—'}</td>
                          <td className="text-sm text-dim">
                            {new Date(item.createdAt).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="card-footer">
                <span className="text-sm text-dim">
                  Showing {showingFrom.toLocaleString()}–{showingTo.toLocaleString()} of{' '}
                  {itemTotal.toLocaleString()}
                </span>
                <span className="spacer" />
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={cursorStack.length === 0}
                  onClick={() =>
                    setParams(
                      {
                        cursors: cursorStack.slice(0, -1).join(','),
                        cursorValues: encodeCursorValues(cursorValueStack.slice(0, -1)),
                      },
                      { resetPage: false },
                    )
                  }
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={showingTo >= itemTotal}
                  onClick={() => {
                    // The next page starts after the last row on this one -
                    // there is no "page 12" to jump to with keyset paging,
                    // only "one further than where we are now". Sorted by
                    // anything other than `written`, that "further" needs the
                    // sorted column's own value alongside the id tie-breaker.
                    const lastItem = items[items.length - 1];
                    if (lastItem === undefined) return;
                    const updates = { cursors: [...cursorStack, lastItem.id].join(',') };
                    if (sortBy !== 'written') {
                      const field = SORT_VALUE_FIELD[sortBy];
                      updates.cursorValues = encodeCursorValues([
                        ...cursorValueStack,
                        String(lastItem[field]),
                      ]);
                    }
                    setParams(updates, { resetPage: false });
                  }}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </section>
      </div>

      {openCompany ? (
        <div className="scrim" onClick={closeCompanyProfile} role="presentation">
          <div
            className="dialog dialog-xl company-modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Company registry profile"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="dialog-header">
              <span className="dialog-title">Registry information</span>
              <button
                type="button"
                className="btn btn-ghost btn-icon dialog-close"
                onClick={closeCompanyProfile}
                aria-label="Close"
              >
                <IconClose />
              </button>
            </div>
            <div className="company-modal-body">
              <CompanyProfile companyNumber={openCompany} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
